import Add16 from '@carbon/icons-react/es/add/16';
import { Space } from 'antd';
import { memoize } from 'lodash';
import React, {
  FC,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Button } from 'components/atoms/button';
import { IconShell } from 'components/atoms/icon-shell';
import { InputType } from 'components/atoms/input';
import { AddIcon } from 'components/molecules/new-row/styles';
import { AddFormTemplatePopover } from 'components/organisms/forms/form-components';
import { BodyShort01, Text03 } from 'components/styles';
import {
  InsertDirection,
  ProjectTemplatePhaseData,
  ProjectTemplateTaskData,
} from 'data/templates/projects';
import {
  createPhaseInProjectTemplate,
  deletePhaseInProjectTemplate,
} from 'data/templates/projects/actions';
import {
  createProjectTemplateSubTask,
  createProjectTemplateTask,
} from 'data/templates/projects/slices';
import { useTranslation } from 'hooks/use-translation';
import { useFormTemplatesListQuery } from 'queries/form-template';
import { queueMacrotask } from 'utils/macrotask';
import { floatUid } from 'utils/uid';
import { unreachable } from 'utils/unreachable';
import { useTemplateTaskCommonActions } from '../../commons/task';
import { CellRendererParams } from '../types';
import { RenderProjectPhaseName } from './project-phase-name';
import { HoverHighlight, NewPhaseRow, NewRow } from './styles';
import { RenderTaskName } from './task-name';

type RenderNewTaskRowProps = Omit<CellRendererParams, 'data'> & {
  data: ProjectTemplateTaskData;
};

const RenderNewTaskRow = forwardRef<unknown, RenderNewTaskRowProps>(
  ({ data, node }, ref) => {
    const addTaskPlaceholder = useTranslation('templates.list.add_task');
    const dispatch = useDispatch();
    const inputRef = useRef<InputType>(null);

    useImperativeHandle(ref, () => {
      return {
        getValue: () => {
          return data.title;
        },
        afterGuiAttached: () => {
          inputRef?.current?.focus();
          inputRef?.current?.select?.();
        },
      };
    });

    const { onDeleteTask } = useTemplateTaskCommonActions(data.data);

    /**
     * This method is a complicated UX where:
     * 1. If a task name is added initially - it is the last row item per phase. For which, focus out
     *    should not remove the item
     * 2. If a task was manually added by user in between, it should be removed from the table on blur if
     *    the task name is empty and the task is new
     * 3. If a task was added by hitting enter, then if the item was added from last row, then add additional
     *    row below it by invoking the right saga action
     * 4. If a task was added in between, then that shouldn't add an additional row.
     */
    const handleName = memoize(async (latestValue = '') => {
      const id = floatUid();

      const payload = {
        taskName: latestValue,
        taskId: id,
        isNew: false,
      };

      if (!latestValue) {
        if (node.level > 1 || (node.level === 1 && !node.lastChild)) {
          return onDeleteTask();
        }

        return Promise.resolve();
      }

      if (data.data.projectPhase) {
        dispatch(
          createProjectTemplateTask({
            task: payload,
            projectPhaseId: data.data.projectPhase.projectPhaseId,
            insertAt: {
              id: data.data.taskId,
              dir: 0,
            },
          }),
        );
      } else {
        dispatch(
          createProjectTemplateSubTask({
            task: payload,
            parentTaskId: data.data.parentTask.taskId,
            insertAt: {
              id: data.data.taskId,
              dir: 0,
            },
          }),
        );
      }
      window.setTimeout(() => {
        const nameColumn = document.querySelector(
          `[row-id="${data.id}"] [col-id="ag-Grid-AutoColumn"]`,
        ) as HTMLElement | undefined;

        if (nameColumn) {
          nameColumn.click();
        }
      }, 50);

      return Promise.resolve();
    });

    return (
      <NewRow
        clearOnBlur
        placeholder={addTaskPlaceholder}
        onAdd={handleName}
        onAddMultiple={(values) => {
          values.forEach(handleName);

          return Promise.resolve();
        }}
        onBlur={handleName}
        inputRef={inputRef}
        bulkAddType="tasks"
      />
    );
  },
);

type RenderNewPhaseRowProps = Omit<CellRendererParams, 'data'> & {
  data: ProjectTemplatePhaseData;
};

const RenderNewPhaseRow = forwardRef<unknown, RenderNewPhaseRowProps>(
  ({ data, node, api, column }, ref) => {
    const projectPhase = data.data;
    const insertAt =
      data.insertAt === null
        ? undefined
        : {
            id: projectPhase.projectPhaseId,
            direction: InsertDirection.Above,
          };

    const dispatch = useDispatch();
    const inputRef = useRef<InputType>(null);

    useImperativeHandle(ref, () => {
      return {
        getValue: () => {
          return data.title;
        },
        afterGuiAttached: () => {
          inputRef?.current?.focus();
          inputRef?.current?.select?.();
        },
      };
    });

    const handlePhaseNameChange = memoize(async (phaseName?: string) => {
      if (phaseName) {
        dispatch(
          createPhaseInProjectTemplate({
            projectPhase: {
              projectPhaseName: phaseName,
              tasks: [],
              private: false,
            },
            insertAt,
            handleSuccess(newProjectPhase) {
              queueMacrotask(() => {
                api.forEachNode((gridNode, rowIndex) => {
                  const isNewlyAddedPhase =
                    newProjectPhase.projectPhaseId ===
                    gridNode.data.data.projectPhaseId;

                  if (isNewlyAddedPhase) {
                    const newPhaseTaskIndex = rowIndex + 1;

                    api.startEditingCell({
                      colKey: column,
                      rowIndex: newPhaseTaskIndex,
                    });
                  }
                });
              });
            },
          }),
        );
      } else if (!node.lastChild && projectPhase.isNew) {
        dispatch(deletePhaseInProjectTemplate(projectPhase));
      }
    });

    return (
      <NewPhaseRow
        clearOnBlur
        onAdd={handlePhaseNameChange}
        onBlur={handlePhaseNameChange}
        placeholder="Add new phase"
        inputRef={inputRef}
      />
    );
  },
);

const Placeholder = styled.div`
  ${Text03};
  ${BodyShort01};
  font-weight: var(--font-weight-normal);
`;

const AddTaskPlaceHolder = styled.div`
  ${Text03};
  ${BodyShort01};
  font-weight: var(--font-weight-normal);
  color: var(--blue-60);
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;
const AddTaskSeparationMessage = styled.div`
  color: var(--gray-40);
`;
const AddTaskPaneButton = styled(Button)`
  padding: 0px;
`;

const AddNewTask: FC<
  Omit<CellRendererParams, 'data'> & { data: ProjectTemplateTaskData }
> = ({ data }) => {
  const id = floatUid();
  const [visible, setVisible] = useState(false);

  const dispatch = useDispatch();
  const { data: formTemplates, isLoading: isFormTemplatesLoading } =
    useFormTemplatesListQuery({
      refetchOnMount: 'always',
    });

  const createFormTask = (selectedFormTemplate: {
    label: string;
    value: number;
  }) => {
    if (data.data.projectPhase) {
      dispatch(
        createProjectTemplateTask({
          task: {
            taskName: selectedFormTemplate.label,
            formTemplateId: selectedFormTemplate.value,
            taskId: id,
          },
          projectPhaseId: data.data.projectPhase.projectPhaseId,
          insertAt: {
            id: data.data.taskId,
            dir: 0,
          },
        }),
      );
    }

    setVisible(false);
  };

  return (
    <Space size={0}>
      <AddTaskPlaceHolder>
        <HoverHighlight>
          <Add16 />
          <AddTaskPaneButton
            size="small"
            type="link"
            data-cy="templates.list.add_task"
          >
            <FormattedMessage id="templates.list.add_task" />
          </AddTaskPaneButton>
        </HoverHighlight>
        <AddTaskSeparationMessage>
          <FormattedMessage id="or" />
        </AddTaskSeparationMessage>
        <HoverHighlight>
          <AddFormTemplatePopover
            visible={visible}
            setVisible={setVisible}
            formTemplates={formTemplates}
            isLoading={isFormTemplatesLoading}
            createForm={(v) => createFormTask(v)}
          >
            <AddTaskPaneButton
              size="small"
              type="link"
              ref={(ref) => {
                if (!ref) {
                  return;
                }
                // eslint-disable-next-line no-param-reassign
                ref.onclick = (e) => {
                  setVisible(true);
                  e.stopPropagation();
                };
              }}
            >
              <FormattedMessage id="forms.response.add_form" />
            </AddTaskPaneButton>
          </AddFormTemplatePopover>
        </HoverHighlight>
      </AddTaskPlaceHolder>
    </Space>
  );
};

const AddNewPhase = () => {
  return (
    <Space size={0} data-cy="template-add-new-phase">
      <IconShell icon={<AddIcon />} />
      <Placeholder>
        <FormattedMessage id="templates.list.add_phase" />
      </Placeholder>
    </Space>
  );
};

export const StatusNameCell: FC<CellRendererParams> = ({ data, ...rest }) => {
  switch (data.type) {
    case 'PROJECT_PHASE':
      return data.data.isNew ? (
        <AddNewPhase />
      ) : (
        <RenderProjectPhaseName record={data} />
      );
    case 'TASK':
      return data.data.isNew ? (
        <AddNewTask data={data} {...rest} />
      ) : (
        <RenderTaskName data={data} {...rest} />
      );
    default:
      return unreachable(data);
  }
};

export const NameEditor = forwardRef<unknown, CellRendererParams>(
  ({ data, ...rest }, ref) => {
    switch (data.type) {
      case 'PROJECT_PHASE':
        return <RenderNewPhaseRow data={data} {...rest} ref={ref} />;
      case 'TASK':
        return <RenderNewTaskRow data={data} {...rest} ref={ref} />;
      default:
        return unreachable(data);
    }
  },
);
